import { Injectable } from '@angular/core';
import { ProjectModelWithRelations } from '@rappider/rappider-sdk';
import { ProjectChannelMessage } from '@rappider/rappider-sdk';
import { isArray, isObject } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class AIPromptService {

  private readonly AI_SENDER_ID = '00000000-0000-0000-0000-000000000001';

  /**
   * Generate project model summary for AI prompt
   */
  generateProjectModelSummaryForAiPrompt(projectModels: ProjectModelWithRelations[]): string {
    if (!projectModels || projectModels.length === 0) {
      return 'No models provided.';
    }

    const defaultFieldsExplanation = `
        Default fields included in every model:
        - id: uuid v4, unique identifier for the record (string)
        - createdDate: Date
        - createdBy: string
        - createdById: string
        - updatedDate: Date
        - updatedBy: string
        - updatedById: string
        - deletedDate: Date
        - deletedBy: string
        - deletedById: string
        - isDeleted: boolean
      `;

    let summary = projectModels
      .map((model) => {
        const modelInfo = [
          `${model.name}`,
          `${model.description || 'No description provided.'}\n`,
          `Fields of ${model.name}:`,
        ];

        const fieldsList = Array.isArray(model.fields)
          ? model.fields?.filter(f => !f.isMixinProperty)
            .map((field) => `- ${field.name}: ${field.type}${field.isArray ? '[]' : ''}`)
            .join('\n')
          : 'No fields available.';

        return `${modelInfo.join('\n')}\n${fieldsList}`;
      })
      .join('\n\n---\n\n');

    summary = `${summary}\n\n${defaultFieldsExplanation}\n\n`;

    return summary;
  }

  /**
   * Build contextual prompt for data model generation
   */
  buildDataModelContextualPrompt(
    newMessage: string,
    messages: ProjectChannelMessage[],
    projectModels?: ProjectModelWithRelations[],
    editingModel?: ProjectModelWithRelations
  ): string {
    if (!messages || messages.length === 0) {
      return newMessage;
    }

    const history = messages
      .map(message => {
        if (message.senderId === this.AI_SENDER_ID) {
          let aiResponse = `AI replied: ${message.message}`;
          if (message.responseData && (isArray(message.responseData) || isObject(message.responseData))) {
            aiResponse += '\n Data Models in the AI response: ' + JSON.stringify(message.responseData, null, 2) + '\n';
          }
          return aiResponse;
        } else {
          let userMessage = `User asked: ${message.message}`;
          if (message.attachedFiles && message.attachedFiles.length > 0) {
            const fileInfo = message.attachedFiles.map((file: File) => `- ${file.name}`).join('\n');
            userMessage += `\n\nFiles attached:\n${fileInfo}`;
          }
          return userMessage;
        }
      })
      .join('\n\n');

    let prompt = `
      This was the previous message content:

      """
      <history>
      ${history}
      </history>
      """
    `;

      if (editingModel) {
        prompt += `

        IMPORTANT: You are currently in EDIT MODE for the "${editingModel.name}" model.

        Current model details:
        """
        <editingModel>
        ${this.generateProjectModelSummaryForAiPrompt([editingModel])}
        </editingModel>
        """

        Your task is to UPDATE the existing "${editingModel.name}" model based on the user's request.
        DO NOT create a new model. Instead, modify the existing model by:

        FIELD OPERATIONS:
        - Adding new fields to the model
        - Removing existing fields from the model
        - Updating existing field types, properties, or descriptions
        - Changing field requirements (required/optional)
        - Modifying field constraints or validations

        RELATION OPERATIONS:
        - Adding new relationships with other models
        - Removing existing relationships
        - Updating relationship types (hasOne, hasMany, belongsTo, etc.)
        - Creating many-to-many relationships through intermediate models
        - Modifying relationship field names or constraints

        MODEL OPERATIONS:
        - Updating the model description
        - Changing model properties or metadata
        - Adding or removing model-level configurations

        IMPORTANT GUIDELINES:
        1. Always preserve existing fields unless explicitly asked to remove them
        2. When adding relations, ensure the target model exists or suggest creating it
        3. Provide clear field-by-field changes in your response
        4. If removing fields, clearly indicate which fields should be deleted
        5. If adding relations, specify the relationship type and target model
        6. Maintain data integrity by considering existing data

        EXAMPLE SCENARIOS:
        - "Add a phone field" → Add new field to existing model
        - "Remove the age field" → Remove specific field from model
        - "Make email required" → Update field properties
        - "Add relation to User model" → Create relationship with another model
        - "Remove relation to Profile" → Delete existing relationship
        - "Change relation type to hasMany" → Update relationship type
        - "Add address field and relation to Address model" → Add field + relation

        Provide the complete updated model structure with all existing fields plus the requested changes.
        `;
    } else {
      prompt += `

      Analyze the user's new message in the context of our conversation history.
      Your task is to generate a new data model or apply updates to an existing one.

      CRITICAL: If the user asks for a data model that already exists, you must inform them it's already available.
      DO NOT create a duplicate. Instead, offer to modify or improve the existing model based on their request.
      `;
    }

    prompt += `

      """
      ${newMessage}
      """
    `;

    return prompt;
  }

  /**
   * Build contextual prompt for entity component generation
   */
  buildEntityComponentContextualPrompt(
    newMessage: string,
    messages: ProjectChannelMessage[],
    selectedProjectModel?: ProjectModelWithRelations
  ): string {
    if (!messages || messages.length === 0) {
      return newMessage;
    }

    // the last message is the new message, so we skip it
    const history = messages?.slice(0, -1)
      .map(m => {
        if (m.senderId === this.AI_SENDER_ID) {
          let aiResponse = 'AI replied with this component code:';
          aiResponse += '\n\n ```' + m.responseData + '\n\n```';
          return aiResponse;
        } else {
          let userMessage = `User asked: ${m.message}`;
          if (m.attachedFiles && m.attachedFiles.length > 0) {
            const fileInfo = m.attachedFiles.map((file: File) => `- ${file.name}`).join('\n');
            userMessage += `\n\nFiles attached:\n${fileInfo}`;
          }
          return userMessage;
        }
      })
      .join('\n\n');

      let message = '';

      if(messages.length > 1) {
        message += `<history> \n\n ${history} \n\n</history>`;
      }

      message += `\n\n <newMessage> \n\n ${newMessage} \n\n </newMessage>`;

      return message;


    // return `
    //   This is the data table schema, use this schema to create a component for the entity.

    //   """
    //   <dataModelSchema>
    //   ${selectedProjectModel ?
    //     this.generateProjectModelSummaryForAiPrompt([selectedProjectModel]) :
    //     'No data models available.'}
    //   </dataModelSchema>
    //   """

    //   This was the previous message content:

    //   """
    //   <history>
    //   ${history}
    //   </history>
    //   """

    //   Analyze the user's new message in the context of our conversation history.
    //   Your task is to generate a new data model or apply updates to an existing one.


    //   """
    //   ${newMessage}
    //   """
    // `;
  }

  /**
   * Build entity component description with model schema
   */
  buildEntityComponentDescription(
    basePrompt: string,
    selectedProjectModel?: ProjectModelWithRelations,
    files?: File[]
  ): string {
    let description = basePrompt;

    // if (selectedProjectModel) {
    //   description += `\n\nThe component will be used to display the entity items. The **entity name** is "${selectedProjectModel.name}".`;

    //   description += `\n\n We will use the component to display our entity item or items when we fetch data from database for this entity. You have the creative freedom to decide which fields from the entity schema are necessary for the UI. Do not try to add inputs for the entity fields as we will transform the entity data as the component expect. However we are providing entity schema so that you can decide to build the component to display necessary entity data. You might not need every single field. For example, you can omit 'id' or 'created/updated/deleted' fields from the display unless they are critical. `;

    //   description += '\n\n  **Entity Schema:**: \n\n' +
    //     this.generateProjectModelSummaryForAiPrompt([selectedProjectModel]);
    // }

    if (files?.length) {
      const fileInfo = files.map((file: File) => `- ${file.name}`).join('\n');
      description += `\n\nFiles attached to this message:\n${fileInfo}`;
    }

    return description;
  }
}
